import { Button, Card, createListCollection, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { memo, useMemo } from 'react';

import SelectField from '@features/spend/SelectField';
import { allCashBalances, CashBalance } from 'helpers/fakeData';
import { DownloadIcon, UploadIcon } from 'vectors';

interface Props {
  currency: string;
  onCurrencyChange: (details: { value: string[] }) => void;
}

const formatBalance = (balance: CashBalance): string => {
  if (balance.isCrypto) {
    const amount = balance.amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${amount} ${balance.currency}`;
  }
  const amount = balance.amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `${balance.currency} ${amount}`;
};

const CashBalanceCard = ({ currency, onCurrencyChange }: Props) => {
  const currencies = useMemo(
    () =>
      createListCollection({
        items: allCashBalances.map((balance) => ({ value: balance.currency, label: balance.currency })),
      }),
    [],
  );

  const balance = allCashBalances.find((item) => item.currency === currency);

  return (
    <Card.Root
      w="full"
      h="fit-content"
      bg="surface.muted"
      borderColor="border.light"
      px={3}
      py={4}
      gap={2}
      borderRadius="md"
    >
      <Card.Header p={0}>
        <Flex alignItems="center" justifyContent="space-between" gap={2}>
          <Heading size="sm">Cash Balance</Heading>
          <Flex w="100px">
            <SelectField
              collection={currencies}
              placeholder={currency}
              value={[currency]}
              onValueChange={onCurrencyChange}
            />
          </Flex>
        </Flex>
      </Card.Header>
      <Card.Body p={0}>
        <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
          <Text fontSize="2xl" fontWeight="bold">
            {balance != null ? formatBalance(balance) : '—'}
          </Text>
          <Flex ml="auto" gap={2}>
            <Button variant="solid" borderRadius="lg" size="2xs">
              Deposit <DownloadIcon />
            </Button>
            <Button variant="outline" borderRadius="lg" size="2xs" bgColor="surface.accent">
              Withdraw <UploadIcon />
            </Button>
          </Flex>
        </Stack>
      </Card.Body>
    </Card.Root>
  );
};

export default memo(CashBalanceCard);
